// https://en.wikipedia.org/wiki/Program_transformation

import { Utils } from '../core/utils';
import { TextViewNode, TextViewNodeKind } from './text.view.div.ast';

export function mergeAdjacentMarkers(node: TextViewNode): void
{
  const mergedChildren: Array<TextViewNode> = [];

  for (const child of node.children || []) {
    if (!(child.kind === TextViewNodeKind.Selection &&
          child.previous &&
          child.previous.kind === TextViewNodeKind.Selection))
    {
      mergeAdjacentMarkers(child);
      mergedChildren.push(child);
    }
  }

  node.children = mergedChildren;
}

export function removeContentlessWords(node: TextViewNode): void
{
  const filteredChildren: Array<TextViewNode> = [];

  for (const child of node.children || []) {
    if (! (child.kind === TextViewNodeKind.WordSequence &&
           child.content === ''))
    {
      removeContentlessWords(child);
      filteredChildren.push(child);
    }
  }

  node.children = filteredChildren;
}

export function markDepth(node: TextViewNode, depth: number = 0): void
{
  node.depth = depth;

  for (const child of node.children || []) {
    markDepth(child, depth + 1);
  }
}

export function insertSiblingLinks(node: TextViewNode): void
{
  if (! node.children) {
    return;
  }

  for (let i = 0; i < node.children.length; ++i) {
    if (i > 0) {
      node.children[i].previous = node.children[i - 1];
    }

    if (i < node.children.length - 1) {
      node.children[i].next = node.children[i + 1];
    }

    insertSiblingLinks(node.children[i]);
  }
}

export function markIgnoredLinebreaks(node: TextViewNode): void
{
  if (node.kind !== TextViewNodeKind.Line) {
    return;
  }

  if (! node.next) {
    node.ignoreLineBreak = true;
  }

  for (const child of node.children || []) {
    markIgnoredLinebreaks(child);
  }
}

export function IEH_flattenNodeHierarchy(node: TextViewNode): void
{
  // The ugliest hacks are always there to accomodate IEs misguided ways.
  // This functions recognizes the fact, that IE11 likes to nest paragraphs:
  //   <p><p>foo</p><p>bar</p></p>
  //
  // Which is wrong and meaningless:
  //   http://w3c.github.io/html/single-page.html#phrasing-content-2
  //
  // To mitigate this madness it should be transformed to something like this:
  //   <p>foo</p><p>bar</p>
  //
  // This is done by black magic and should be removed as soon as possible!

  if (! node.children) {
    return;
  }

  const restructureNode = (n: TextViewNode): Array<TextViewNode> => {
    if (! n.children || n.children.length < 1) {
      return [n];
    }

    const empty = () => ({
      kind: TextViewNodeKind.Line,
      content: '',
      children: []
    });

    const acc: Array<TextViewNode> = [];
    let cur = empty();

    const pushCurrent = () => {
      if (cur.children.length > 0) {
        acc.push(cur);
        cur = empty();
      }
    };

    for (const c of n.children || []) {
      IEH_flattenNodeHierarchy(c);

      if (c.kind === TextViewNodeKind.Line && (c.depth || 0) > 1) {
        pushCurrent();
        acc.push(c);
      } else {
        cur.children.push(c);
      }
    }

    pushCurrent();

    return acc;
  };

  node.children = Utils.flattenArray(node.children.map(restructureNode));
}

export function resolveProxyNodes(node: TextViewNode): void
{
  if (! node.children) {
    return;
  }

  const resolvedChildren = [];

  for (const child of node.children) {
    if (child.kind === TextViewNodeKind.Proxy) {
      for (const grandChild of child.children || []) {
        resolvedChildren.push(grandChild);
      }
    } else {
      resolvedChildren.push(child);
      resolveProxyNodes(child);
    }
  }

  node.children = resolvedChildren;
}
